<script setup lang="ts">
// Supplier document library — one component, two modes:
//   manage: supplier CRUD (upload, rename, delete, download)
//   view:   advisor read-only (list + download) on the supplier profile
import { ref, onMounted } from 'vue'
import { useAuth } from '../../composables/useAuth'
import { API_CONFIG } from '../../config/api'
import { FileText, Loader2, Pencil, Trash2 } from 'lucide-vue-next'

const props = defineProps<{ supplierId: string; mode: 'manage' | 'view' }>()
const { getAccessTokenSilently } = useAuth()

interface SupplierDoc { id: string; title: string; file_name: string; file_size: number; mime_type: string; created_at: string }

const docs = ref<SupplierDoc[]>([])
const loading = ref(true)
const uploading = ref(false)
const error = ref('')
const renamingId = ref<string | null>(null)
const renameText = ref('')

const baseUrl = () => props.mode === 'manage'
  ? `${API_CONFIG.baseURL}/api/suppliers/${props.supplierId}/documents`
  : `${API_CONFIG.baseURL}/api/advisors/me/suppliers/${props.supplierId}/documents`

const fmtSize = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`

async function authed(path = '', init: RequestInit = {}) {
  const token = await getAccessTokenSilently()
  return fetch(baseUrl() + path, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } })
}

async function load() {
  loading.value = true
  try {
    const res = await authed()
    if (res.ok) docs.value = await res.json()
  } catch (e) { console.error('Documents load failed', e) }
  finally { loading.value = false }
}

async function onPick(ev: Event) {
  const input = ev.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  uploading.value = true
  error.value = ''
  try {
    const fd = new FormData()
    fd.append('file', file)
    const res = await authed('', { method: 'POST', body: fd })
    if (!res.ok) { const j = await res.json().catch(() => ({})); error.value = j.error || 'Upload failed'; return }
    await load()
  } catch { error.value = 'Upload failed' }
  finally { uploading.value = false }
}

async function download(doc: SupplierDoc) {
  try {
    const res = await authed(`/${doc.id}/download`)
    const j = await res.json()
    if (j.url) window.open(j.url, '_blank')
  } catch (e) { console.error('Download failed', e) }
}

function startRename(doc: SupplierDoc) { renamingId.value = doc.id; renameText.value = doc.title }
async function saveRename(doc: SupplierDoc) {
  const title = renameText.value.trim()
  renamingId.value = null
  if (!title || title === doc.title) return
  try {
    await authed(`/${doc.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }) })
    doc.title = title
  } catch (e) { console.error('Rename failed', e) }
}

async function remove(doc: SupplierDoc) {
  if (!confirm(`Delete "${doc.title}"? Advisors will no longer see it.`)) return
  try {
    await authed(`/${doc.id}`, { method: 'DELETE' })
    docs.value = docs.value.filter(d => d.id !== doc.id)
  } catch (e) { console.error('Delete failed', e) }
}

onMounted(load)
</script>

<template>
  <div v-if="mode === 'manage' || loading || docs.length" class="bg-cc-elevated border border-cc-border rounded-xl p-5">
    <div class="flex items-center justify-between mb-3">
      <h2 class="text-sm font-semibold text-cc-text flex items-center gap-1.5"><FileText :size="15" /> Documents</h2>
      <label v-if="mode === 'manage'" class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-cc text-xs font-semibold bg-cc-primary text-cc-btn-text hover:opacity-90 cursor-pointer">
        <Loader2 v-if="uploading" :size="12" class="animate-spin" />
        {{ uploading ? 'Uploading…' : '+ Upload' }}
        <input type="file" class="hidden" :disabled="uploading" @change="onPick"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg" />
      </label>
    </div>
    <p v-if="mode === 'manage'" class="text-xs text-cc-text-tertiary -mt-1 mb-3">Datasheets, one-pagers, case studies — advisors see these on your profile.</p>
    <p v-if="error" class="text-sm text-red-500 mb-2">{{ error }}</p>

    <div v-if="loading" class="py-6 text-center text-cc-text-tertiary text-sm">Loading…</div>
    <div v-else-if="!docs.length" class="py-6 text-center text-cc-text-tertiary text-sm">No documents yet.</div>
    <div v-else class="divide-y divide-cc-border/60">
      <div v-for="doc in docs" :key="doc.id" class="flex items-center gap-3 py-2.5">
        <div class="min-w-0 flex-1">
          <template v-if="renamingId === doc.id">
            <input v-model="renameText" @keydown.enter="saveRename(doc)" @blur="saveRename(doc)" autofocus
              class="w-full px-2 py-1 bg-cc-surface border border-cc-border rounded text-sm text-cc-text outline-none focus:ring-1 focus:ring-cc-primary" />
          </template>
          <template v-else>
            <button @click="download(doc)" class="text-sm font-medium text-cc-primary hover:underline truncate block max-w-full text-left">{{ doc.title }}</button>
            <p class="text-xs text-cc-text-tertiary">{{ doc.file_name }} · {{ fmtSize(doc.file_size) }}</p>
          </template>
        </div>
        <div v-if="mode === 'manage'" class="flex gap-1 shrink-0">
          <button @click="startRename(doc)" class="p-1.5 rounded text-cc-text-tertiary hover:text-cc-text hover:bg-cc-hover" title="Rename"><Pencil :size="13" /></button>
          <button @click="remove(doc)" class="p-1.5 rounded text-cc-text-tertiary hover:text-red-500 hover:bg-red-500/10" title="Delete"><Trash2 :size="13" /></button>
        </div>
      </div>
    </div>
  </div>
</template>
